/**
 * payablesDataSource.ts — WatermelonDB → PayablesAgingInput bridge.
 *
 * Like receivables, payables are a position as of now, not activity in a range:
 * load every supplier that carries a balance, plus their full ledger, and let
 * the pure builder reconstruct the aging by FIFO.
 *
 * `suppliers` and `supplier_ledger` are scoped by `merchant_id` (not store) —
 * a supplier balance is a merchant-wide obligation, settled from any store.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Supplier, SupplierLedger } from '@/db/models/Inventory';
import type { PayablesAgingInput } from './payablesAging';

/** Load a merchant's outstanding payables as of `asOf` (defaults to now). */
export async function loadPayables(
  merchantId: string,
  asOf: Date = new Date(),
): Promise<PayablesAgingInput> {
  const suppliers = await database.get<Supplier>('suppliers').query(
    Q.where('merchant_id', merchantId),
    Q.where('is_deleted', false),
    Q.where('current_balance', Q.gt(0)),
  ).fetch();

  const supplierIds = suppliers.map((s) => s.id);

  // The ledger is queried per-supplier set so a merchant with many inactive
  // suppliers doesn't drag in ledgers we won't age.
  const ledgerRows = await fetchInChunks(supplierIds, (batch) =>
    database.get<SupplierLedger>('supplier_ledger').query(
      Q.where('supplier_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );

  return {
    asOf,
    suppliers: suppliers.map((s) => ({
      id: s.id,
      name: s.name,
      phone: s.phone,
      creditDays: s.creditDays,
      creditLimit: s.creditLimit,
      currentBalance: s.currentBalance,
    })),
    ledger: ledgerRows.map((l) => ({
      supplierId: l.supplierId,
      type: l.type,
      amount: l.amount,
      date: l.transactionDate,
    })),
  };
}
